// ─────────────────────────────────────────────────────────────────────────────
// PLAN PAYLOAD CONTRACT (#5036 BACKEND LEAD — the two payload contracts;
// frozen by the BACKEND LEAD contract amendment accepting UI FABLE's R1–R3 +
// conventions). `scenario_set` and `decision_package`: the two additive,
// optional payloads a producer mounts onto the public response DTO so
// UI-2/UI-3 and S2 (#5042) / S3 (#5043) build to one shape.
//
// Three rules govern every field below:
//   1. Every number is a placeholder into an engine-bound `numericBindings[]`
//      entry (the anti-fabrication fence + `displayValue` apply unchanged) —
//      never a raw literal.
//   2. Every string is producer-authored, reader-safe text (passes
//      `readerSafeGaps`) — never assembled by the client.
//   3. Sections exist only when a producer emits them (Law 3); absence is not
//      an error, it is "this producer did not run" — no client reconstruction.
// Plus the frozen structural rule from the amendment: when `decisionPackage`
// is present, scenario sets appear ONLY nested on `members[].scenarioSet`;
// top-level `scenarioSets[]` is for standalone scenario-only answers — never
// both on the same response.
//
// #5140 Q6 REPAIR — these declarations used to live inside `ask-helix.ts`, a
// 2.6k-line barrel. They are one contract with one reader, so they live in one
// focused module and `ask-helix.ts` re-exports them; nothing importing from
// `@helix/lib` changes. The barrel is smaller for it.
// ─────────────────────────────────────────────────────────────────────────────

// TYPE-ONLY on purpose: the badge union is declared beside the provenance DTO it
// belongs to, and a type import is erased, so re-exporting this module from that
// same barrel creates no runtime cycle.
import type { AskHelixProvenanceBadgeDto } from '../../ask-helix';

export const ASK_HELIX_SCENARIO_CASE_EMPHASES = Object.freeze([
  'recommended',
  'alternative',
] as const);

export type AskHelixScenarioCaseEmphasisDto =
  (typeof ASK_HELIX_SCENARIO_CASE_EMPHASES)[number];

export const ASK_HELIX_DECISION_PACKAGE_DISPOSITIONS = Object.freeze([
  'mandate_now',
  'validate',
  'gated',
] as const);

export type AskHelixDecisionPackageDispositionDto =
  (typeof ASK_HELIX_DECISION_PACKAGE_DISPOSITIONS)[number];

// #5036 amendment R2: closed set; the UI never parses signs out of
// `displayValue` to orient a waterfall bar — the producer states direction.
export const ASK_HELIX_BRIDGE_STEP_DIRECTIONS = Object.freeze([
  'increase',
  'decrease',
  'subtotal',
] as const);

export type AskHelixBridgeStepDirectionDto =
  (typeof ASK_HELIX_BRIDGE_STEP_DIRECTIONS)[number];

// Provenance reuses the standing show-with-badge closed set of four
// (`AskHelixProvenanceBadgeDto`, #4449 GSD) verbatim — no second enum minted
// for the same four values.
export type AskHelixDecisionPackageMemberProvenanceDto = AskHelixProvenanceBadgeDto;

export const ASK_HELIX_SCENARIO_CASE_INPUT_KINDS = Object.freeze([
  'binding',
  'literal',
] as const);

export type AskHelixScenarioCaseInputKindDto =
  (typeof ASK_HELIX_SCENARIO_CASE_INPUT_KINDS)[number];

/**
 * ONE input to a case — and the ONE numeric authority rule this payload has.
 *
 * #5140 Q6 P0 REPAIR. This used to be `{ value: number | string | boolean;
 * unit; displayValue }`: a raw number PLUS a pre-formatted string, published
 * beside the engine-bound `numericBindings[]` the same card renders from. That
 * is a second numeric path — a client could render the figure without ever
 * touching the evidence/binding fence, and two authorities can disagree.
 *
 * So a NUMERIC input is now a REFERENCE: `placeholder` names an entry in
 * `numericBindings[]`, and the reader renders that binding's server-authored
 * `displayValue` under its `displayLabel`, exactly as it renders every other
 * figure on the card. A numeric input whose binding did not survive the
 * engine-bound fence is simply not published — never downgraded to a literal.
 *
 * `literal` survives for the genuinely non-numeric: reader-authored text and
 * producer-stated flags. Its `value` is `string | boolean` at the type level,
 * so a number cannot re-enter through it.
 */
export type AskHelixScenarioCaseInputDto =
  | {
      /** The capability requirement this input answers. A key, never rendered as prose. */
      requirementId: string;
      kind: 'binding';
      /** Placeholder into `numericBindings[]`. Joined by the reader, never parsed. */
      placeholder: string;
    }
  | {
      requirementId: string;
      kind: 'literal';
      /** Producer-authored, reader-safe and NON-numeric by type. */
      value: string | boolean;
    };

/**
 * The producer's own close, carried unchanged.
 *
 * #5140 Q6 P0 REPAIR. The capability that ran the governed cases already owns
 * the recommendation and the basis for it — `ExecutiveAnswerRecommendationAuthoritySource`
 * calls that authority `producer_scenario`. Publishing the cases without it
 * forced the narrator to either invent the close or drop it, and a contract
 * declaring `recommendationPolicy: 'required'` cannot be honoured by silence.
 *
 * Every field is producer-authored reader prose copied verbatim. No manager,
 * planner or narrator writes into this block, and nothing downstream re-derives
 * it: a consumer that has it renders it, and one that does not shows nothing.
 *
 * #5140 Q6 P1 — it is published at the TOP LEVEL of the response, not nested in
 * `scenarioSets[]`. Nesting made delivery conditional on a complete scenario
 * set, and this branch withholds the set when no numeric binding survives the
 * fence — so the exact partial case the P0 repair describes (lawful sections,
 * evidence and assumptions, every figure unresolved) lost the producer's close
 * before it ever reached the reader. A close that only survives when the numbers
 * do is not reader delivery.
 */
export type AskHelixScenarioRecommendationDto = {
  /** The producer's own id for the case it recommends. */
  recommendedCaseId: string;
  /** The recommendation itself. */
  statement: string;
  /** WHY — the producer's stated basis for it. */
  basis: string;
  /** What the recommendation gives up, when the producer states it. */
  tradeoff?: string;
};

export const ASK_HELIX_RECOMMENDATION_ROLES = Object.freeze([
  'direct',
  'supporting',
  'constraint',
] as const);

export type AskHelixRecommendationRoleDto =
  (typeof ASK_HELIX_RECOMMENDATION_ROLES)[number];

/**
 * ONE producer close, WITH the identity of the operation that authored it.
 *
 * #5140 P1 REPAIR. The first pass published a single `recommendation` and
 * dropped BOTH closes whenever two governed operations each authored one. That
 * is a silent suppression on exactly the multi-capability answers the manager
 * exists to produce: refusing to RANK is correct, refusing to SHOW is not.
 *
 * So every close is published, each carrying `callId` / `capabilityId` / `role`
 * so a reader can attribute it and can find the DIRECT operation's close without
 * the manager ever scoring one against another. `role` is the plan's own
 * declared operation role, copied — never a manager judgement about importance.
 *
 * `statement`, `basis` and `tradeoff` are producer-authored reader prose copied
 * verbatim and, by contract, DIGIT-FREE: this text is rendered outside
 * `publish.ts`, so it is not a second numeric channel. A producer that cannot
 * author a number-free close authors none, and the missing close becomes a
 * NAMED GAP rather than a silent omission.
 */
export type AskHelixAnswerRecommendationDto = {
  /** The governed call that authored this close. */
  callId: string;
  /** The capability that owns it. */
  capabilityId: string;
  /** The plan's declared role for that operation, copied verbatim. */
  role: AskHelixRecommendationRoleDto;
  /** The producer's own id for the case it recommends. */
  recommendedCaseId: string;
  /** The recommendation itself. Digit-free by contract. */
  statement: string;
  /** WHY — the producer's stated basis. Digit-free by contract. */
  basis: string;
  /** What it gives up, when the producer states it. Digit-free by contract. */
  tradeoff?: string;
};

export interface AskHelixScenarioCaseDto {
  // Producer-stable, e.g. "conservative" | "recommended" | "stretch" —
  // closed by the producer, not by the UI.
  caseId: string;
  // Reader-facing.
  label: string;
  emphasis: AskHelixScenarioCaseEmphasisDto;
  inputs: AskHelixScenarioCaseInputDto[];
  // Placeholders of this case's engine-bound bindings (headline first).
  bindings: string[];
  // "recommend when …" — producer-authored, reader-safe.
  triggerLine?: string;
}

export interface AskHelixScenarioSetDto {
  capabilityId: string;
  intentId: string;
  leverLabel: string;
  domain: string;
  // #5036 UI FABLE R1: the lever's baseline binding placeholder, when the
  // producer has one. Optional; absent → card renders cases only.
  baselinePlaceholder?: string;
  // 1..3; the reader's cited value (if any) is one case, and is the
  // recommended one unless the producer says otherwise.
  cases: AskHelixScenarioCaseDto[];
}

export interface AskHelixDecisionPackageMemberDto {
  leverLabel: string;
  // #5043 review P2: OMITTED rather than empty when the governed catalog card
  // carries none. An empty string is a value a renderer would print.
  domain?: string;
  capabilityId?: string;
  // The member's engine-bound contribution placeholder.
  contributionPlaceholder: string;
  // Solver member disposition (closed).
  disposition: AskHelixDecisionPackageDispositionDto;
  // The standing provenance badge set (#4449 GSD).
  provenance: AskHelixDecisionPackageMemberProvenanceDto;
  // Only present when the producer supplies them.
  owner?: string;
  timing?: string;
  // S2's payload, mounted per lever when that capability emitted one.
  // #5036 convention (C2): scenario sets nest on members ONLY when
  // `decisionPackage` is present — never duplicated on the top-level
  // `scenarioSets[]` too.
  scenarioSet?: AskHelixScenarioSetDto;
}

export interface AskHelixDecisionPackageDto {
  objectiveMetricKey: string;
  baselinePlaceholder: string;
  targetPlaceholder: string;
  gapPlaceholder: string;
  totalPlaceholder: string;
  targetFillPlaceholder: string;
  residualPlaceholder: string;
  // Solver order, authoritative (Law 8) — the UI preserves it verbatim, never
  // re-sorts (#5036 convention C1).
  members: AskHelixDecisionPackageMemberDto[];
  // Solver deconfliction / ineligibility, named.
  excludedCandidates: Array<{ leverLabel: string; reason: string }>;
  // Server-supplied waterfall steps, in order.
  bridgeSteps: Array<{
    label: string;
    placeholder: string;
    direction: AskHelixBridgeStepDirectionDto;
  }>;
}
